import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Divider, Spin, Typography, Space, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import logo from '@static/images/favicon-logo.svg';
import sawlogo from '@static/images/saw-icon.svg';
import LoginContent from '@/pages/Workspace/component/LoginContent';
import { isSafari, isSAWDomain, isSAWPlan, isSIAPlan } from '@/utils'
import Tenant, { Realm, ClientType, TenantRef } from './tenant';
import styles from './index.module.less';
import { Email } from './email';

const { Text } = Typography;

// teams app 应用等第三方登录类型
export enum MicroAppType {
	Teamsapp = 'teamsapp',
	Slackapp = 'slackapp',
}

// teams app 进行登录模式是打开一个新标签进行登录
// 登录完成以后将token信息发送到原有标签中
// 在原有标签中写入
export enum MicroAppAction {
	Auth = 'auth',
}

enum LoginMethod {
	Email = 'Email',
	Domain = 'Domain',
}

declare global {
	interface Window {
    onMobile: any;
	}
}


function getRegisterUrls(plan_type: string): {[index: string]: string} {
	if (isSIAPlan(plan_type)) {
		return {
			dev: 'https://sia-reg.dev.ztw.splashtop.com',
			stage: 'https://sia-reg.stage.ztw.splashtop.com',
			prod: 'https://sia-reg.ssw.splashtop.com',
		}
	} else if (isSAWPlan(plan_type) || isSAWDomain()) {
		return {
			dev: 'https://register.dev.shield.splashtop.com/',
			stage: 'https://register.stage.shield.splashtop.com/',
			prod: 'https://register.shield.splashtop.com/',
		}
	}
	return {};
}


const Workspace = () => {
	const { t } = useTranslation();
	const workspaceUri = new URL(window.location.href);
	const [searchParams] = useSearchParams();
	const plan_type = searchParams.get('plan_type') || '';
	const notAutoLinkToRecentTenant = workspaceUri.searchParams.get('auto') === 'false' || isSAWDomain() || isSAWPlan(plan_type);
	const [pageVisible, setPageVisible] = React.useState<boolean>(notAutoLinkToRecentTenant);
	const tenantForm = useRef<TenantRef>(null);
	const [realms, setRealms] = React.useState(JSON.parse(localStorage.realms || '[]'));
	const [domain, setDomain] = React.useState(window.location.host);
	const [launchLoading, setLaunchLoading] = React.useState<any>({});
	const [loginMethod, setLoginMethod] = useState(() => {
		// signin 开头的域名是给Splashtop Shield 使用 email查找tenant使用的
		if ((isSAWDomain() || isSAWPlan(plan_type)) && /^signin/.test(location.host)) {
			return LoginMethod.Email;
		}

		return LoginMethod.Domain;
	});

	React.useEffect(() => {
		// 在realms为空或不需要自动跳转的情况下
		if (!realms?.length || notAutoLinkToRecentTenant) {
			!pageVisible && setPageVisible(true);

			const url = new URL(window.location.href);
			const [, ...hosts] = url.host.split('.');

			setDomain(hosts.join('.'));
		} else {
			// 获取到最近一个使用的tenang
			const [recentRealm] = realms || [];

			if (recentRealm) {
				onLaunch(recentRealm)?.catch(() => {
					setPageVisible(true);
				});
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function onLaunch(realm: Realm) {
		launchLoading[realm.name] = true;
		setLaunchLoading({ ...launchLoading });
		// fill workspace
		tenantForm.current?.setFieldsValue({ realm: realm.name });
		// auto direct to keycloak login
		return tenantForm.current?.triggerRealm().finally(() => {
			launchLoading[realm.name] = false;
			setLaunchLoading({ ...launchLoading });
		});
	}

	function onDelete(realm: Realm) {
		const nextRealms = realms.filter((item: Realm) => item.name !== realm.name);

		setRealms(nextRealms);

		// 存储到本地
		localStorage.realms = JSON.stringify(nextRealms);
	}

	const appTitle = useMemo(() => {
		if (isSAWPlan(plan_type) || isSAWDomain()) {
			return (
			  	<Space direction="vertical" size={8}>
					<Text strong style={{ fontSize: 20 }}>Splashtop Shield</Text>
				</Space>
			)
		} else if (isSIAPlan(plan_type)) {
			return "Secure Internet Access";
		} else {
			return t('workspace.title');
		}
  	}, [searchParams, plan_type])

	const registerUrl = useMemo(() => {
		if (isSIAPlan(plan_type) || isSAWPlan(plan_type) || isSAWDomain()) {
			return getRegisterUrls(plan_type)?.[process.env.ENV as string || 'prod'];
		} else {
			return process.env.REGISTER_URL
		}
  	}, [searchParams, plan_type]);

	const subTitle = useMemo(() => {
		if (isSAWPlan(plan_type) || isSAWDomain()) {
			if (loginMethod === LoginMethod.Domain) {
				return t('savw.subtitle');
			} else if (loginMethod === LoginMethod.Email) {
				return t('workspace.email.subtitle');
			}
		} else {
			return t('workspace.subtitle');
		}
	}, [t, plan_type, loginMethod]);

	const changeLoginMethod = useCallback((method: LoginMethod) => {
		setLoginMethod(method);
		localStorage.setItem('login_method', method);
	}, []);

	return (
		<>
			{
				!pageVisible
				&&
				<div className={styles.tenantLoading}>
					<Spin spinning tip="Loading" indicator={<LoadingOutlined />}><></></Spin>
				</div>
			}
			<div style={{ display: pageVisible ? 'block' : 'none' }}>
				<LoginContent
					title={appTitle}
					subTitle={subTitle}
				>
					{
						loginMethod === LoginMethod.Domain && <Tenant plan_type={plan_type} realms={realms} ref={tenantForm} />
					}
					{
						loginMethod === LoginMethod.Email && <Email />
					}
					{
						!!realms.length
						&&
							<div className={styles.recent}>
								<div className={styles.phrase}>{!(isSAWPlan(plan_type) || isSAWDomain()) ? t('workspace.recent') : "Recent Domain"}:</div>
								<div className={styles.list}>
									{realms.map((realm: Realm) => {
										return (
											<div key={realm.name} className={styles.item}>
												<span className={styles.icon}>
												<img
													style={{ width: '14px' }}
													src={(isSAWPlan(plan_type) || isSAWDomain()) ? sawlogo : logo}
													alt="logo-icon"
												/>
												</span>
												<div className={styles.text}>
													<p className={styles.name}>
														<span>{realm.name}</span>
														<Button
															className={styles.button}
															onClick={() => onDelete(realm)}
															type="link"
															size="small"
														>
															<DeleteOutlined />
														</Button>
													</p>
													<p className={styles.email}>
														{realm.domain || `${realm.name}.${domain}`}
													</p>
												</div>
												<div className={styles.action}>
													<Button type="primary" size="small" loading={launchLoading[realm.name]} ghost onClick={() => onLaunch(realm)}>{t('workspace.launch')}</Button>
												</div>
											</div>
										);
									})}
								</div>
							</div>
					}

					{
						!(isSafari() && (workspaceUri.searchParams.get('auth') === ClientType.Ext))
						&&
						<>
							<Divider style={{ margin: '16px 0 14px' }}>
								<span style={{ fontSize: '12px', fontWeight: 400 }}>{t('workspace.or')}</span>
							</Divider>
							<Row gutter={[0, 16]}>
								<Col span={24}>
									<div style={{ fontSize: '14px' }}>
										<span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{t('workspace.account')}?&nbsp;</span>
										<Button
											target="_blank"
											href={registerUrl}
											style={{ paddingLeft: 0, paddingRight: 0 }}
											type="link"
											size="small"
										>
											{t('workspace.create')}
										</Button>
									</div>
								</Col>
							</Row>
						</>
					}
				</LoginContent>
			</div>
		</>
	);
};

export default Workspace;
